import React, { useState } from 'react';
import SubmitButton from '../../../components/Common/SubmitButton';
import { AgeConfigModel, AGE_GROUP_LABELS } from './services/StoryConfig.service';

interface StoryConfigFormProps {
  setShowForm: (value: boolean) => void;
  ageKey: string;
  ageConfig: AgeConfigModel;
  onSave: (ageKey: string, data: AgeConfigModel) => Promise<void>;
}

const StoryConfigForm: React.FC<StoryConfigFormProps> = ({
  setShowForm,
  ageKey,
  ageConfig,
  onSave,
}) => {
  const [loader, setLoader] = useState(false);
  const [form, setForm] = useState<AgeConfigModel>({
    words_total: ageConfig.words_total || '',
    pages_total: ageConfig.pages_total || '',
    max_text_length: ageConfig.max_text_length || '',
    rows_total: ageConfig.rows_total || '',
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoader(true);
    try {
      await onSave(ageKey, form);
    } finally {
      setLoader(false);
    }
  };

  const isValid = !!form.words_total;
  const label = AGE_GROUP_LABELS[ageKey] || ageKey;

  return (
    <div className="bg-white rounded-lg p-6 max-w-xl">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-gray-800">
          Edit Reading Level — {label}
        </h2>
        <button
          onClick={() => setShowForm(false)}
          className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
        >
          &times;
        </button>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="words_total" className="block text-sm font-medium text-gray-700 mb-1">
            Words Per Page *
          </label>
          <input
            type="number"
            id="words_total"
            name="words_total"
            value={form.words_total}
            onChange={handleChange}
            placeholder="e.g. 40"
            className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="1"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum number of words allowed per page for this reading level. Used in AI prompts and validation.
          </p>
        </div>

        <div>
          <label htmlFor="max_text_length" className="block text-sm font-medium text-gray-700 mb-1">
            Max Text Length (characters)
          </label>
          <input
            type="number"
            id="max_text_length"
            name="max_text_length"
            value={form.max_text_length}
            onChange={handleChange}
            placeholder="e.g. 240"
            className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Maximum character count per page text area. Enforced in the edit text panel and self-written stories.
          </p>
        </div>

        <div>
          <label htmlFor="rows_total" className="block text-sm font-medium text-gray-700 mb-1">
            Textarea Rows
          </label>
          <input
            type="number"
            id="rows_total"
            name="rows_total"
            value={form.rows_total}
            onChange={handleChange}
            placeholder="e.g. 11"
            className="w-full border border-gray-300 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
            min="1"
          />
          <p className="text-xs text-gray-500 mt-1">
            Number of visible rows in the page text area (cosmetic).
          </p>
        </div>

        <div className="flex justify-end gap-3 mt-2">
          <button
            type="button"
            onClick={() => setShowForm(false)}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <SubmitButton title="Update" loader={loader} disable={!isValid} />
        </div>
      </form>
    </div>
  );
};

export default StoryConfigForm;
